<template>
  <div class="admin-noticeboard p-4 bg-white rounded-lg shadow-md relative flex flex-col gap-4 h-full">
    <div class="justify-between flex border-b-2 pb-2 items-center">
      <span class="text-lg  font-bold text-primary">
        Upcoming Events
      </span>
      <Button
        v-if="can(Capabilities.DASHBOARD.CREATE_NEW_EVENTS)"
        label="Add New Event"
        icon="pi pi-plus"
        size="small"
        @click="showEventModal = true"
      />
    </div>

    <div
      v-if="events.length === 0"
      class="flex flex-col gap-1 text-gray-500 h-full items-center justify-center"
    >
      <Icon
        :name="BaseIcon.EMPTY"
        size="40"
      />
      <p>
        No upcoming events.
      </p>
    </div>

    <div
      v-else
      ref="eventsList"
      class="overflow-y-auto show-y-thin-scrollbar px-4 flex-auto  space-y-4"
    >
      <div
        v-for="event in events"
        :key="event.id"
        class="w-full border rounded-lg p-4 space-y-4"
      >
        <div>
          <div class="flex w-full items-center justify-between gap-2 mb-2">
            <h3 class="text-lg font-semibold">
              {{ event.title }}
            </h3>
            <div
              class="event-type-badge px-2 py-1 rounded text-xs"
              :class="{
                'bg-green-100 text-green-800': event.eventType === 'IN_PERSON',
                'bg-blue-100 text-blue-800': event.eventType === 'VIRTUAL',
                'bg-orange-100 text-orange-800': event.eventType === 'HYBRID',
              }"
            >
              {{ event.eventType }}
            </div>
          </div>
          <p class="text-gray-600 mb-2">
            {{ event.description }}
          </p>
          <div class="event-details text-sm text-gray-500 space-y-1">
            <div class="flex align-items-center gap-2">
              <Icon name="lucide:calendar" />
              <span>Date: {{ formatDate(event?.eventDate) }}</span>
            </div>
            <div class="flex align-items-center gap-2">
              <Icon name="lucide:map-pin" />
              <span>Location: {{ event.address?.fullAddress }}</span>
            </div>
          </div>
        </div>
        <div class="event-actions flex space-x-3">
          <Button
            severity="secondary"
            variant="outlined"
            size="small"
            @click="eventForm = event, showEventModal = true"
          >
            <Icon name="lucide:edit" /> Edit
          </Button>
          <Button
            severity="danger"
            variant="outlined"
            size="small"
            @click="deleteEvent(event.id)"
          >
            <Icon name="lucide:trash" /> Delete
          </Button>
        </div>
      </div>
    </div>

    <DashboardUpcomingEventsCreateAndEdit
      v-model:showEventModal="showEventModal"
      v-model:eventForm="eventForm"
      @init="getAllEvents"
    />
  </div>
</template>

<script setup lang="ts">
import { BaseIcon } from '~/types/baseIcon'

const { can, Capabilities } = useRolePermissions()
const { getAllEvents: getAllEventsFromDB, createEvent: createEventToDB, deleteEvent: deleteEventInDB } = useDashboardEvents()

// STATES
const events = ref([])
const isLoading = ref(false)
const eventForm = ref({})
const showEventModal = ref(false)

// EVENTS
const getAllEvents = async () => {
  isLoading.value = true
  const eventRes = await getAllEventsFromDB()
  events.value = eventRes?.data
  isLoading.value = false
}

const eventsList = ref(null)
onMounted(async () => {
  await getAllEvents()
})

const deleteEvent = async (eventId) => {
  if (confirm('Are you sure you want to delete this event?')) {
    await deleteEventInDB(eventId)
    await getAllEvents()
  }
}

const { locale } = useI18n()
const formatDate = (date) => {
  return new Date(date).toLocaleDateString(locale.value, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}
</script>
